import { useRef, useState } from "react";
import { cn } from "@/lib/cn";
import { Dialog } from "./Dialog";

export interface FotoCarrusel {
  id: number;
  src: string;
  alt: string;
  /** "Renders", "Plano": los chips de arriba saltan a la primera de cada grupo. */
  grupo: string;
  /** "cubrir" llena el cuadro (renders); "contener" la muestra entera (planos). */
  ajuste?: "cubrir" | "contener";
}

export interface CarruselProps {
  etiqueta: string;
  fotos: FotoCarrusel[];
}

/**
 * Las imágenes grandes, de a una, deslizando de costado (en la compu, con las flechas). Arriba, un chip por grupo para
 * saltar ("Renders · Plano"); abajo, "2 / 5". Tocar la imagen la abre más grande. Sin fotos no dibuja nada.
 */
export function Carrusel({ etiqueta, fotos }: CarruselProps) {
  const pista = useRef<HTMLDivElement>(null);
  const [indice, setIndice] = useState(0);
  const [ampliada, setAmpliada] = useState<FotoCarrusel | null>(null);
  if (fotos.length === 0) return null;

  const actual = Math.min(indice, fotos.length - 1);
  const grupos = [...new Set(fotos.map((f) => f.grupo))];
  const ir = (i: number) => {
    const destino = Math.max(0, Math.min(fotos.length - 1, i));
    setIndice(destino);
    const el = pista.current;
    el?.scrollTo?.({ left: destino * el.clientWidth, behavior: "smooth" });
  };
  const alDeslizar = () => {
    const el = pista.current;
    if (el && el.clientWidth > 0) setIndice(Math.round(el.scrollLeft / el.clientWidth));
  };

  return (
    <div role="region" aria-roledescription="carrusel" aria-label={etiqueta} data-carrusel className="relative overflow-hidden rounded-tarjeta bg-superficie-2">
      <div ref={pista} onScroll={alDeslizar} className="flex snap-x snap-mandatory overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {fotos.map((f, i) => (
          <button
            key={f.id}
            type="button"
            aria-label={`Ver más grande: ${f.alt}`}
            aria-hidden={i !== actual}
            tabIndex={i === actual ? 0 : -1}
            onClick={() => setAmpliada(f)}
            className={cn("aspect-[4/3] w-full shrink-0 snap-center focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primario", f.ajuste === "contener" && "bg-white")}
          >
            <img src={f.src} alt={f.alt} loading={i === 0 ? "eager" : "lazy"} className={cn("size-full", f.ajuste === "contener" ? "object-contain p-2" : "object-cover")} />
          </button>
        ))}
      </div>
      {grupos.length > 1 && (
        <div className="absolute left-2 top-2 flex gap-1">
          {grupos.map((g) => {
            const activo = fotos[actual]?.grupo === g;
            return (
              <button
                key={g}
                type="button"
                aria-pressed={activo}
                onClick={() => ir(fotos.findIndex((f) => f.grupo === g))}
                className={cn(
                  "rounded-full px-2.5 py-1 text-[0.75rem] font-medium shadow-tarjeta backdrop-blur",
                  activo ? "bg-texto text-superficie" : "bg-superficie/85 text-texto",
                )}
              >
                {g}
              </button>
            );
          })}
        </div>
      )}
      {fotos.length > 1 && (
        <>
          <button type="button" aria-label="Imagen anterior" disabled={actual === 0} onClick={() => ir(actual - 1)} className="absolute left-2 top-1/2 hidden size-9 -translate-y-1/2 place-items-center rounded-full bg-superficie/90 text-texto shadow-tarjeta disabled:opacity-0 sm:grid">
            ‹
          </button>
          <button type="button" aria-label="Imagen siguiente" disabled={actual === fotos.length - 1} onClick={() => ir(actual + 1)} className="absolute right-2 top-1/2 hidden size-9 -translate-y-1/2 place-items-center rounded-full bg-superficie/90 text-texto shadow-tarjeta disabled:opacity-0 sm:grid">
            ›
          </button>
          <span aria-live="polite" className="absolute bottom-2 right-2 rounded-full bg-texto/70 px-2 py-0.5 text-[0.75rem] font-medium tabular-nums text-superficie">
            {actual + 1} / {fotos.length}
          </span>
        </>
      )}
      <Dialog abierto={ampliada !== null} onCambio={(abierto) => !abierto && setAmpliada(null)} titulo={ampliada?.alt ?? ""} ancho="amplio">
        {ampliada && <img src={ampliada.src} alt={ampliada.alt} className="max-h-[75dvh] w-full rounded-control bg-white object-contain" />}
      </Dialog>
    </div>
  );
}
